import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { EventTimeline, SpecialProperty, TimelineEvent } from "@/components/timeline";
import { AssistantNoteReviewSummarySchema } from "@/domains/openapi/types";
import { api } from "@/modules/api";

interface AssistantNoteReviewsTimelineProps {
  sessionId: string;
}

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const buildSummary = (review: AssistantNoteReviewSummarySchema): string => {
  const parts: string[] = [];
  const title = review.note_title ?? review.note_id;
  if (title) {
    parts.push(title);
  }
  parts.push(`${review.thread_count} ${review.thread_count === 1 ? "thread" : "threads"}`);
  parts.push(`${review.task_count} ${review.task_count === 1 ? "task" : "tasks"}`);
  parts.push(`${review.reminder_count} ${review.reminder_count === 1 ? "reminder" : "reminders"}`);
  return parts.join(" | ");
};

const buildEvents = (reviews: AssistantNoteReviewSummarySchema[]): TimelineEvent[] => {
  return reviews.map(review => ({
    id: review.id,
    kind: "note_review",
    occurred_at: review.created_at,
    summary: buildSummary(review),
    properties_json: {
      note_id: review.note_id,
      note_title: review.note_title,
      context: review.context,
      message: review.message,
      thread_count: review.thread_count,
      task_count: review.task_count,
      reminder_count: review.reminder_count,
      threads: review.threads ?? [],
      metadata: review.metadata,
    },
  }));
};

const getSpecialProperties = (properties: Record<string, unknown>): SpecialProperty[] => {
  const threads = Array.isArray(properties.threads) ? properties.threads : [];
  const entries: (SpecialProperty | null)[] = threads.map(thread => {
    if (!thread || typeof thread !== "object") {
      return null;
    }
    const threadId = asString((thread as { id?: unknown }).id);
    if (!threadId) {
      return null;
    }
    return {
      key: `thread:${threadId}`,
      label: "Thread",
      id: threadId,
      route: atlasRoutes.assistantThreadsView({ params: { threadId } }).path,
    };
  });
  return entries.filter((value): value is SpecialProperty => Boolean(value));
};

export const AssistantNoteReviewsTimeline = ({ sessionId }: AssistantNoteReviewsTimelineProps) => {
  const query = useQuery({
    queryKey: ["assistant-note-reviews", sessionId],
    queryFn: () =>
      api.post("/internal/v1/assistant-note-reviews/search", {
        body: {
          session_id: sessionId,
          limit: 200,
          offset: 0,
        },
      }),
    enabled: Boolean(sessionId),
    staleTime: 30 * 1000,
  });

  const reviews = query.data?.data?.results ?? [];
  const total = query.data?.data?.total ?? 0;

  return (
    <EventTimeline<TimelineEvent>
      events={buildEvents(reviews)}
      total={total}
      isLoading={query.isLoading}
      isError={query.isError}
      emptyMessage="No note reviews recorded yet."
      getSpecialProperties={getSpecialProperties}
      sortDirection="desc"
      title="Notes"
    />
  );
};
